import React, { useState, useEffect } from 'react';
import { DataProvider, useData, DOCTOR_INFO } from './context/DataContext';
import { LoaderProvider } from './context/LoaderContext';
import LoginPage from './pages/LoginPage';
import ReceptionistPanel from './panels/ReceptionistPanel';
import AdminPanel from './panels/AdminPanel';
import DoctorPanel from './panels/DoctorPanel';
import LabPanel from './panels/LabPanel';
import FinancePanel from './panels/FinancePanel';

// Navigation
const Navigation = ({ currentPage, setCurrentPage, onStaffLogin }) => {
  const navItems = ['Home', 'Services', 'About', 'Contact'];
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 py-2">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="Logo" className="w-12 h-12" />
            <div className="hidden sm:block">
              <span className="font-semibold text-blue-900 font-heading">Dr Ruqqia <span className="text-blue-600 italic">Sultana</span></span>
              <p className="text-xs text-blue-500">Clinic & Maternity Home</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {navItems.map((item) => (
              <button key={item} onClick={() => setCurrentPage(item.toLowerCase())}
                className={`px-3 py-1.5 text-xs font-bold tracking-wide transition-all duration-300 rounded-full whitespace-nowrap ${currentPage === item.toLowerCase() ? 'bg-blue-600 text-white shadow-md' : 'text-gray-700 hover:bg-blue-100 hover:text-blue-700 border border-gray-200'}`}>
                {item}
              </button>
            ))}
            <button onClick={onStaffLogin} className="px-3 py-1.5 text-xs font-bold tracking-wide transition-all duration-300 rounded-full whitespace-nowrap bg-gray-800 text-white hover:bg-gray-700 ml-2">
              Staff Login
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Image Slider Component
// Image Slider Component
const ImageSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const slides = [
  { image: '/pics/a.jpg',   title: '24/7 Emergency Services',    desc: 'Always here when you need us' },
  { image: '/pics/b.jpg',   title: 'Modern Maternity Wards',     desc: 'Comfortable care for mothers' },
  { image: '/pics/c.jpeg',  title: 'Expert Medical Team',        desc: 'Highly qualified professionals' },
  { image: '/pics/d.jpeg',  title: 'State-of-the-Art Equipment', desc: 'Latest medical technology' },
  { image: '/pics/e.jpeg',  title: 'Painless Delivery',          desc: 'Safe and comfortable deliveries' },
  { image: '/pics/f.jpeg',  title: 'Nursery 24/7',               desc: 'For booked patients only' },
  { image: '/pics/g.jpeg',  title: 'OPD Services',               desc: 'Mon–Sat, 3 PM to 8 PM' },
  { image: '/pics/h.jpeg',  title: 'Patient Comfort',            desc: 'Your comfort is our priority' },
  { image: '/pics/i.jpeg',  title: 'Clean & Safe Environment',   desc: 'Hygienic facility standards' },
  { image: '/pics/j.jpeg',  title: 'Dedicated Nursing Staff',    desc: 'Round the clock care' },
  { image: '/pics/k.jpeg',  title: 'Labour Room',                desc: 'Fully equipped labour room' },
  { image: '/pics/l.jpeg',  title: 'Postnatal Care',             desc: 'Care after delivery' },
  { image: '/pics/m.jpeg',  title: 'Ultrasound & Diagnostics',   desc: 'On-site diagnostic services' },
  { image: '/pics/n.jpeg',  title: 'Gynaecology Services',       desc: 'Complete women\'s healthcare' },
];
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const goToPrev = () => setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
  const goToNext = () => setCurrentIndex((prev) => (prev + 1) % slides.length);

  return (
    <div className="relative w-full h-64 md:h-80 overflow-hidden rounded-2xl">
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-all duration-700 ease-in-out ${
            index === currentIndex ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full'
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
className="w-full h-full object-contain bg-black/20"
            onError={(e) => { e.target.src = 'https://via.placeholder.com/800x400?text=No+Image'; }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <h3 className="text-xl md:text-2xl font-bold font-heading">{slide.title}</h3>
            <p className="text-sm md:text-base text-white/80">{slide.desc}</p>
          </div>
        </div>
      ))}

      {/* Left Arrow */}
      <button
        onClick={goToPrev}
        className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white w-8 h-8 rounded-full flex items-center justify-center transition-all z-10"
      >
        ‹
      </button>

      {/* Right Arrow */}
      <button
        onClick={goToNext}
        className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white w-8 h-8 rounded-full flex items-center justify-center transition-all z-10"
      >
        ›
      </button>

      {/* Dots */}
      <div className="absolute bottom-4 right-4 flex gap-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`h-2 rounded-full transition-all ${index === currentIndex ? 'bg-white w-6' : 'bg-white/50 w-2'}`}
          />
        ))}
      </div>
    </div>
  );
};
// Home Page
const HomePage = ({ setCurrentPage }) => {
  const { news } = useData();
  const activeNews = news.filter(n => n.active);

  return (
    <section className="relative min-h-screen overflow-hidden">
      <div className="absolute inset-0 bg-gradient-primary" />
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-96 h-96 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #fff 0%, transparent 70%)', top: '10%', right: '10%' }} />
        <div className="absolute w-64 h-64 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #fff 0%, transparent 70%)', bottom: '20%', left: '5%' }} />
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-24 pb-12">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-white">
            <p className="text-sm tracking-widest uppercase mb-4 text-blue-200">Welcome to</p>
            <h1 className="text-4xl lg:text-5xl font-bold leading-tight mb-4 font-heading">
              {DOCTOR_INFO.name}
              <span className="block text-blue-200 text-2xl mt-2 font-normal">{DOCTOR_INFO.title}</span>
            </h1>
            <div className="text-blue-200 text-sm mb-6 space-y-1">
              {DOCTOR_INFO.degrees.map((deg, i) => <span key={i} className="inline-block mr-2">{deg} •</span>)}
              <p className="mt-2">PMDC: {DOCTOR_INFO.pmdc} | {DOCTOR_INFO.college}</p>
            </div>
            <p className="text-lg text-blue-100 mb-8 leading-relaxed">
              Specialized gynecology and maternity healthcare services in Abbottabad.
            </p>
            
            <div className="flex flex-wrap gap-4 mb-8">
              <a href={`tel:${DOCTOR_INFO.phone}`} className="px-6 py-3 bg-white text-blue-700 font-semibold rounded-full hover:bg-blue-50 transition-all duration-300 shadow-lg">
                📞 {DOCTOR_INFO.phone}
              </a>
              <button onClick={() => setCurrentPage('services')} className="px-6 py-3 border-2 border-white/50 text-white font-semibold rounded-full hover:bg-white/10 transition-all">
                Our Services
              </button>
            </div>
            
            <div className="bg-white/10 rounded-xl p-4 backdrop-blur">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-blue-300">📍 Address:</span><p className="text-white">Mandian, Abbottabad</p></div>
                <div><span className="text-blue-300">⏰ OPD:</span><p className="text-white">{DOCTOR_INFO.opdTiming}</p></div>
                <div><span className="text-blue-300">🚨 Emergency:</span><p className="text-white">24/7 Available</p></div>
                <div><span className="text-blue-300">📅 Holiday:</span><p className="text-white">Sunday (except emergency)</p></div>
              </div>
            </div>
          </div>
          
          <div className="space-y-6">
            <div className="glass rounded-3xl p-6">
              <h3 className="text-white text-xl font-bold mb-4 font-heading">Our Specialties</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: '👶', title: 'Maternity Care' },
                  { icon: '💊', title: 'Gynae Procedures' },
                  { icon: '🏥', title: 'Painless Delivery' },
                  { icon: '🍼', title: 'Nursery 24/7', subtitle: '(For booked patients only)' }
                ].map((item, i) => (
                  <div key={i} className="bg-white/10 rounded-xl p-4 hover:bg-white/20 transition-all">
                    <span className="text-2xl mb-2 block">{item.icon}</span>
                    <h4 className="text-white font-semibold text-sm">{item.title}</h4>
                    {item.subtitle && <p className="text-blue-200 text-xs mt-1">{item.subtitle}</p>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* News Section - Below Address/Timing */}
        {activeNews.length > 0 && (
          <div className="mt-8">
            <div className="bg-white/30 backdrop-blur-sm rounded-2xl p-5 border border-white/20 max-w-2xl">
              <p className="text-blue-100 text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                <span>📢</span> LATEST NEWS
              </p>
              <h4 className="text-white font-bold text-lg">{activeNews[0].title}</h4>
              <p className="text-white/80 text-sm mt-2">{activeNews[0].content.substring(0, 150)}...</p>
            </div>
          </div>
        )}

        {/* Image Slider Section */}
        <div className="mt-12">
          <h3 className="text-white text-xl font-bold mb-4 font-heading text-center">Our Facilities</h3>
          <ImageSlider />
        </div>
      </div>
    </section>
  );
};

// Services Page
const ServicesPage = () => {
  const services = [
    { icon: '🏥', title: 'Hysterectomy', description: 'Surgical removal of the uterus performed with utmost care.' },
    { icon: '💊', title: 'Myomectomy', description: 'Surgical procedure to remove uterine fibroids.' },
    { icon: '🩺', title: 'Uterine Prolapse Surgery', description: 'Corrective surgery for uterine prolapse.' },
    { icon: '✨', title: 'Urinary Incontinence Surgery', description: 'Surgical treatment for bladder control.' },
    { icon: '🔬', title: 'Laparoscopy', description: 'Minimally invasive surgical technique.' },
    { icon: '🔍', title: 'Hysteroscopy', description: 'Examination of the uterine cavity.' },
    { icon: '👨‍👩‍👧', title: 'Family Planning', description: 'Comprehensive family planning services.' },
    { icon: '👶', title: 'Painless Delivery', description: 'Modern painless delivery options.' }
  ];

  return (
    <section className="min-h-screen pt-24 pb-20" style={{ background: 'linear-gradient(to bottom, #e3f2fd 0%, #ffffff 100%)' }}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4 font-heading">Our <span className="text-blue-600 italic">Services</span></h2>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-blue-100 group card-hover">
              <span className="text-4xl mb-4 block">{service.icon}</span>
              <h3 className="text-lg font-bold text-gray-900 mb-2 font-heading">{service.title}</h3>
              <p className="text-gray-600 text-sm">{service.description}</p>
            </div>
          ))}
        </div>
        
        <div className="mt-12 text-center bg-gradient-primary rounded-3xl p-8">
          <h3 className="text-2xl font-bold text-white mb-4 font-heading">Need a Consultation?</h3>
          <p className="text-blue-100 mb-4">OPD: {DOCTOR_INFO.opdTiming} | Emergency: 24/7</p>
          <a href={`tel:${DOCTOR_INFO.phone}`} className="inline-block px-6 py-3 bg-white text-blue-700 font-semibold rounded-full">
            📞 {DOCTOR_INFO.phone}
          </a>
        </div>
      </div>
    </section>
  );
};

// About Page
const AboutPage = () => (
  <section className="min-h-screen bg-white pt-24 pb-20">
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
        <div>
          <h2 className="text-4xl font-bold text-gray-900 mb-6 font-heading">
            Dedicated to <span className="text-blue-600 italic">Women's Health</span>
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed mb-6">
            {DOCTOR_INFO.clinicName} has been serving women's healthcare needs in Abbottabad with dedication and expertise.
          </p>
          <div className="bg-blue-50 rounded-xl p-6">
            <h3 className="font-bold text-blue-800 mb-3">Qualifications</h3>
            <ul className="space-y-2 text-gray-700">
              {DOCTOR_INFO.degrees.map((deg, i) => (
                <li key={i} className="flex items-center gap-2"><span className="text-blue-500">✓</span> {deg}</li>
              ))}
              <li className="flex items-center gap-2"><span className="text-blue-500">✓</span> PMDC: {DOCTOR_INFO.pmdc}</li>
            </ul>
          </div>
        </div>
        <div className="bg-gradient-primary rounded-3xl p-8 text-white text-center">
          <img src="/logo.svg" alt="Logo" className="w-24 h-24 mx-auto mb-4 bg-white rounded-full p-2" />
          <h3 className="text-xl font-bold">{DOCTOR_INFO.clinicName}</h3>
          <div className="mt-6 grid grid-cols-3 gap-4">
            {[{ number: '15+', label: 'Years' }, { number: '5000+', label: 'Patients' }, { number: '24/7', label: 'Emergency' }].map((stat, i) => (
              <div key={i}><p className="text-2xl font-bold">{stat.number}</p><p className="text-blue-200 text-sm">{stat.label}</p></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Contact Page
const ContactPage = () => (
  <section className="min-h-screen pt-24 pb-20 bg-gradient-light">
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold text-gray-900 mb-4 font-heading">Contact <span className="text-blue-600 italic">Us</span></h2>
      </div>
      
      <div className="grid lg:grid-cols-3 gap-6 mb-12">
        {[
          { icon: '📍', title: 'Visit Us', lines: ['Behind Mubarik Plaza', 'Mansehra Road, Mandian', 'Abbottabad, KPK'] },
          { icon: '📞', title: 'Call Us', lines: [DOCTOR_INFO.phone, 'Emergency: 24/7'] },
          { icon: '⏰', title: 'Timings', lines: ['OPD: 3 PM - 7 PM', 'Mon - Sat', 'Sunday: Closed*'] }
        ].map((item, i) => (
          <div key={i} className="card p-6 text-center card-hover">
            <div className="w-14 h-14 mx-auto mb-4 rounded-xl flex items-center justify-center text-2xl bg-blue-100">{item.icon}</div>
            <h3 className="text-lg font-bold text-gray-900 mb-3 font-heading">{item.title}</h3>
            <div className="space-y-1 text-gray-600 text-sm">{item.lines.map((line, j) => <p key={j}>{line}</p>)}</div>
          </div>
        ))}
      </div>
      
      <div className="bg-gradient-primary rounded-3xl p-8 text-center text-white">
        <h3 className="text-2xl font-bold mb-4 font-heading">{DOCTOR_INFO.clinicName}</h3>
        <p className="text-blue-100 mb-6">{DOCTOR_INFO.address}</p>
        <a href={`tel:${DOCTOR_INFO.phone}`} className="inline-block px-6 py-3 bg-white text-blue-700 font-semibold rounded-full">
          📞 {DOCTOR_INFO.phone}
        </a>
      </div>
    </div>
  </section>
);

// Footer
const Footer = ({ setCurrentPage }) => (
  <footer className="bg-gradient-primary">
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="grid md:grid-cols-4 gap-8">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <img src="/logo.svg" alt="Logo" className="w-12 h-12 bg-white rounded-full p-1" />
            <div>
              <span className="text-white font-bold">Dr Ruqqia Sultana</span>
              <p className="text-blue-200 text-sm">Clinic & Maternity Home</p>
            </div>
          </div>
          <p className="text-blue-200 text-sm">Providing exceptional women's healthcare and maternity services.</p>
        </div>
        <div>
          <h4 className="text-white font-bold mb-4">Quick Links</h4>
          <ul className="space-y-2">
            {['Home', 'Services', 'About', 'Contact'].map((item) => (
              <li key={item}><button onClick={() => setCurrentPage(item.toLowerCase())} className="text-blue-200 hover:text-white text-sm">{item}</button></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-white font-bold mb-4">Contact</h4>
          <ul className="space-y-2 text-blue-200 text-sm">
            <li>📍 Mandian, Abbottabad</li>
            <li>📞 {DOCTOR_INFO.phone}</li>
            <li>⏰ OPD: 3 PM - 7 PM</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/20 mt-8 pt-6 text-center">
        <p className="text-blue-200 text-sm">© {new Date().getFullYear()} {DOCTOR_INFO.clinicName}. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

// Main App Content
const AppContent = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [showLogin, setShowLogin] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null);

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [currentPage]);

  if (loggedInUser) {
    const handleLogout = () => setLoggedInUser(null);
    switch (loggedInUser.role) {
      case 'receptionist': return <ReceptionistPanel user={loggedInUser} onLogout={handleLogout} />;
      case 'admin': return <AdminPanel user={loggedInUser} onLogout={handleLogout} />;
      case 'doctor': return <DoctorPanel user={loggedInUser} onLogout={handleLogout} />;
     case 'lab': return <LabPanel user={loggedInUser} onLogout={handleLogout} />;
      case 'finance': return <FinancePanel user={loggedInUser} onLogout={handleLogout} />;
      default: return null;
    }
  }

  if (showLogin) return <LoginPage onLogin={setLoggedInUser} onBack={() => setShowLogin(false)} />;

  const renderPage = () => {
    switch (currentPage) {
      case 'services': return <ServicesPage />;
      case 'about': return <AboutPage />;
      case 'contact': return <ContactPage />;
      default: return <HomePage setCurrentPage={setCurrentPage} />;
    }
  };

  return (
    <div className="min-h-screen">
      <Navigation currentPage={currentPage} setCurrentPage={setCurrentPage} onStaffLogin={() => setShowLogin(true)} />
      {renderPage()}
      <Footer setCurrentPage={setCurrentPage} />
    </div>
  );
};
function App() {
  return (
    <LoaderProvider>
      <DataProvider>
        <AppContent />
      </DataProvider>
    </LoaderProvider>
  );
}

export default App;
